/** Workspace member Home and account sub-views. Project management lives in Projects. */
import { Suspense } from 'react';
import { Activity } from '@/shared/ui/icons';
import type { User, Company } from '../../types/auth';
import { CollectionTemplate, TemplateSection } from '../../layout-system';
import { ClientOverview } from './dashboard/ClientOverview';
import { lazyChunk } from '../../utils/lazyChunk';

const MembershipView = lazyChunk(() => import('../billing/MembershipView'), (m) => m.MembershipView);
const MessagesView = lazyChunk(() => import('../messages/MessagesView'), (m) => m.MessagesView);

interface ClientDashboardProps {
  user?: User;
  company?: Company;
  currentView?: string;
  onProjectSelect?: (projectId: string, projectName: string) => void;
  pendingChatId?: string | null;
  onChatConsumed?: () => void;
}

export function ClientDashboard({
  user, company, currentView = 'dashboard', onProjectSelect,
  pendingChatId, onChatConsumed,
}: ClientDashboardProps) {
  // ─── Sub-view routing ──────────────────────────────────────────────────────

  if (currentView !== 'dashboard') {
    if (currentView === 'billing') {
      return (
        <Suspense fallback={null}>
          <MembershipView />
        </Suspense>
      );
    }
    if (currentView === 'messages') {
      return (
        <Suspense fallback={null}>
          <MessagesView initialChatId={pendingChatId} onInitialChatConsumed={onChatConsumed} />
        </Suspense>
      );
    }
    return (
      <CollectionTemplate fillHeight inset="flush" contentClassName="h-full min-h-0">
        <TemplateSection
          surface="plain"
          className="flex-1 min-h-0"
          contentClassName="flex h-full min-h-0 items-center justify-center"
        >
          <div className="text-center space-y-3 max-w-md px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-2">
              <Activity className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-[length:var(--text-h3)] font-semibold font-apple text-foreground">Coming Soon</h3>
            <div className="text-[length:var(--text-base)] text-muted-foreground font-apple">
              The <span className="font-medium">{currentView}</span> view is under development.
            </div>
          </div>
        </TemplateSection>
      </CollectionTemplate>
    );
  }

  // ─── Dashboard overview — dense single-screen surface ─────────────────────

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="flex-1 min-h-0">
        <ClientOverview
          user={user}
          company={company}
          onProjectSelect={onProjectSelect}
        />
      </div>
    </div>
  );
}
